import { useRef, type ReactNode } from 'react';
import { usePageReveal } from '../lib/motion';
import { wakeLabel, type Wake } from '../lib/useWake';
import { useElena } from '../state';
import { Icon, type IconName } from './Icon';

export type Route =
  | 'home'
  | 'chat'
  | 'routines'
  | 'games'
  | 'notes'
  | 'calendar'
  | 'server'
  | 'stats'
  | 'integrations'
  | 'widget'
  | 'permissions'
  | 'settings';

interface NavItem {
  route: Route;
  label: string;
  icon: IconName;
}

const groups: Array<{ title: string | null; items: NavItem[] }> = [
  {
    title: null,
    items: [
      { route: 'home', label: 'Početna', icon: 'home' },
      { route: 'chat', label: 'Razgovor', icon: 'chat' },
      { route: 'calendar', label: 'Kalendar', icon: 'calendar' },
      { route: 'notes', label: 'Beleške', icon: 'note' },
      { route: 'routines', label: 'Rutine', icon: 'zap' },
      { route: 'games', label: 'Igre', icon: 'game' },
    ],
  },
  {
    title: 'Kontrola',
    items: [
      { route: 'server', label: 'MILPET server', icon: 'server' },
      { route: 'stats', label: 'Statistika', icon: 'chart' },
    ],
  },
  {
    title: 'Sistem',
    items: [
      { route: 'integrations', label: 'Integracije', icon: 'plug' },
      { route: 'permissions', label: 'Dozvole', icon: 'shield' },
      { route: 'settings', label: 'Podešavanja', icon: 'sliders' },
    ],
  },
];

const tabs: NavItem[] = [
  { route: 'home', label: 'Početna', icon: 'home' },
  { route: 'routines', label: 'Rutine', icon: 'zap' },
  { route: 'server', label: 'Server', icon: 'server' },
  { route: 'settings', label: 'Više', icon: 'sliders' },
];

export function Shell({
  route,
  children,
  onPalette,
  onMic,
  wake,
}: {
  route: Route;
  children: ReactNode;
  onPalette: () => void;
  onMic: () => void;
  wake: Wake;
}) {
  const { connection, status } = useElena();
  const serverOnline = status?.server.online;
  const main = useRef<HTMLElement>(null);
  usePageReveal(main, route);

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Preskoči na sadržaj
      </a>

      <nav className="side" aria-label="Moduli">
        <div className="brand">
          <span className="brand-mark">
            <Icon name="wave" size={24} stroke={2} />
          </span>
          <span className="brand-name">Elena</span>
          <span className="kbd">OS</span>
        </div>

        <button type="button" className="side-search" onClick={onPalette}>
          <Icon name="search" />
          <span className="grow">Komande</span>
          <kbd className="kbd">Ctrl K</kbd>
        </button>

        {groups.map((g) => (
          <div key={g.title ?? 'main'} className="side-group">
            {g.title && <div className="side-title">{g.title}</div>}
            {g.items.map((item) => (
              <a
                key={item.route}
                href={`#/${item.route}`}
                className={`side-link${route === item.route ? ' is-active' : ''}`}
                aria-current={route === item.route ? 'page' : undefined}
              >
                <Icon name={item.icon} />
                <span className="grow">{item.label}</span>
                {item.route === 'server' && serverOnline !== undefined && (
                  <span
                    className={`dot ${serverOnline ? 'on' : 'off'}`}
                    aria-label={serverOnline ? 'online' : 'offline'}
                  />
                )}
              </a>
            ))}
          </div>
        ))}

        <div className="side-foot">
          <button
            type="button"
            className={`wake-toggle${wake.enabled ? ' is-on' : ''}`}
            role="switch"
            aria-checked={wake.enabled}
            onClick={() => wake.setEnabled(!wake.enabled)}
            title={wake.message ?? 'Reci „Hej Elena, …“ bez dugmeta'}
          >
            <span className={`dot ${wakeDot(wake)}`} />
            <span className="grow">Hej Elena</span>
            <span className="muted small">{wakeLabel(wake)}</span>
          </button>
          <span className="row gap-8">
            <span
              className={`dot ${connection === 'online' ? 'on' : connection === 'connecting' ? 'warn' : 'off'}`}
            />
            {connection === 'online'
              ? 'Povezana sa serverom'
              : connection === 'connecting'
                ? 'Povezujem se…'
                : 'Nema veze sa serverom'}
          </span>
          <span className="muted small">
            {status ? `${status.model} · v${status.version}` : ' '}
          </span>
        </div>
      </nav>

      <main ref={main} id="main" className="main" tabIndex={-1}>
        {children}
      </main>

      <nav className="tabbar" aria-label="Glavna navigacija">
        {tabs.slice(0, 2).map((t) => (
          <a
            key={t.route}
            href={`#/${t.route}`}
            className={`tab${route === t.route ? ' is-active' : ''}`}
            aria-current={route === t.route ? 'page' : undefined}
          >
            <Icon name={t.icon} size={22} />
            {t.label}
          </a>
        ))}
        <div className="tab-mic">
          <button
            type="button"
            className="mic-btn big"
            onClick={onMic}
            aria-label="Pričaj sa Elenom"
          >
            <Icon name="mic" size={26} stroke={2} />
          </button>
        </div>
        {tabs.slice(2).map((t) => (
          <a
            key={t.route}
            href={`#/${t.route}`}
            className={`tab${route === t.route ? ' is-active' : ''}`}
            aria-current={route === t.route ? 'page' : undefined}
          >
            <Icon name={t.icon} size={22} />
            {t.label}
          </a>
        ))}
      </nav>
    </div>
  );
}

const wakeDot = (w: Wake) =>
  !w.enabled ? 'idle' : w.state === 'listening' ? 'on' : w.state === 'error' ? 'off' : 'warn';
